import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { CalendarDays, MapPin, Clock, User, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import clsx from 'clsx';

interface AfspraakData {
  hash: string;
  status: string;
  type: string;
  klantNaam: string;
  afspraak: {
    titel: string;
    type: string;
    icon: string;
    datum: string;
    vanUur: string | null;
    totUur: string | null;
    medewerker: string;
    adres: string;
    stad: string;
    postcode: string | null;
  };
  bevestigdOp: string | null;
}

export function AfspraakBevestigingPage() {
  const { hash } = useParams();
  const [actie, setActie] = useState<'idle' | 'bevestigd' | 'herplan' | 'geannuleerd'>('idle');
  const [herplanReden, setHerplanReden] = useState('');
  const [showHerplan, setShowHerplan] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['public-afspraak', hash],
    queryFn: async () => {
      const res = await fetch(`/api/public/afspraak/${hash}`);
      if (!res.ok) throw new Error('Niet gevonden');
      return res.json() as Promise<AfspraakData>;
    },
    enabled: !!hash,
  });

  const bevestig = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/public/afspraak/${hash}/bevestig`, { method: 'POST' });
      if (!res.ok) throw new Error('Fout');
      return res.json();
    },
    onSuccess: () => setActie('bevestigd'),
  });

  const herplan = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/public/afspraak/${hash}/herplan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reden: herplanReden }),
      });
      if (!res.ok) throw new Error('Fout');
      return res.json();
    },
    onSuccess: () => setActie('herplan'),
  });

  const annuleer = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/public/afspraak/${hash}/annuleer`, { method: 'POST' });
      if (!res.ok) throw new Error('Fout');
      return res.json();
    },
    onSuccess: () => setActie('geannuleerd'),
  });

  if (isLoading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (isError || !data) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
        <CalendarDays size={48} className="text-gray-300 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">Afspraak niet gevonden</h1>
        <p className="text-gray-500 text-sm">Deze link is ongeldig of verlopen.</p>
      </div>
    </div>
  );

  const alBevestigd = data.status === 'BEVESTIGD' || actie === 'bevestigd';
  const alGeweigerd = data.status === 'GEWEIGERD' || actie === 'geannuleerd' || actie === 'herplan';

  const datumStr = data.afspraak.datum
    ? new Intl.DateTimeFormat('nl-BE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(data.afspraak.datum))
    : '';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="bg-brand px-6 py-5 text-white">
          <div className="flex items-center gap-2 mb-1">
            <CalendarDays size={20} />
            <span className="text-sm font-medium opacity-90">{data.afspraak.type}</span>
          </div>
          <h1 className="text-xl font-bold">{data.afspraak.titel}</h1>
          <p className="text-sm opacity-80 mt-1">Beste {data.klantNaam}</p>
        </div>

        {/* Details */}
        <div className="p-6 space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CalendarDays size={18} className="text-brand mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium text-gray-900">{datumStr}</div>
                {data.afspraak.vanUur && (
                  <div className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                    <Clock size={14} /> {data.afspraak.vanUur} – {data.afspraak.totUur}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <User size={18} className="text-brand mt-0.5 flex-shrink-0" />
              <div className="text-gray-700">{data.afspraak.medewerker}</div>
            </div>

            {data.afspraak.adres && (
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-brand mt-0.5 flex-shrink-0" />
                <div className="text-gray-700">
                  {data.afspraak.adres}<br />
                  {data.afspraak.postcode && `${data.afspraak.postcode} `}{data.afspraak.stad}
                </div>
              </div>
            )}
          </div>

          {/* Status / Acties */}
          {alBevestigd ? (
            <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-green-700">
              <CheckCircle size={20} />
              <div>
                <div className="font-semibold">Afspraak bevestigd</div>
                <div className="text-xs text-green-600">Bedankt! We verwachten u op de afgesproken datum.</div>
              </div>
            </div>
          ) : alGeweigerd ? (
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600">
              {actie === 'herplan' ? <RefreshCw size={20} /> : <XCircle size={20} />}
              <div>
                <div className="font-semibold">{actie === 'herplan' ? 'Herplanningsverzoek ontvangen' : 'Afspraak geannuleerd'}</div>
                <div className="text-xs">{actie === 'herplan' ? 'We nemen contact op voor een nieuw moment.' : 'We nemen contact op.'}</div>
              </div>
            </div>
          ) : showHerplan ? (
            <div className="space-y-3">
              <textarea
                value={herplanReden}
                onChange={e => setHerplanReden(e.target.value)}
                placeholder="Reden of voorkeursdatum..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-brand/50"
              />
              <div className="flex gap-2">
                <button onClick={() => setShowHerplan(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">
                  Terug
                </button>
                <button onClick={() => herplan.mutate()} disabled={herplan.isPending}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-orange-500 rounded-xl hover:bg-orange-600 disabled:opacity-50">
                  {herplan.isPending ? 'Bezig...' : 'Verzoek versturen'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <button onClick={() => bevestig.mutate()} disabled={bevestig.isPending}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors">
                <CheckCircle size={18} /> {bevestig.isPending ? 'Bezig...' : 'Bevestigen'}
              </button>
              <button onClick={() => setShowHerplan(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-xl hover:bg-orange-100 transition-colors">
                <RefreshCw size={18} /> Herplannen aanvragen
              </button>
              <button onClick={() => annuleer.mutate()} disabled={annuleer.isPending}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors">
                <XCircle size={16} /> {annuleer.isPending ? 'Bezig...' : 'Annuleren'}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 text-center">
          <p className="text-[10px] text-gray-400">Kordaro Planning</p>
        </div>
      </div>
    </div>
  );
}
